export type SearchMode = 'module' | 'facility' | 'survey' | 'invoice' | 'user';

export const SEARCH_PREFIXES: { prefix: string; mode: SearchMode }[] = [
  {prefix: '!', mode: 'survey'},
  {prefix: '#', mode: 'facility'},
  {prefix: '$', mode: 'invoice'},
  {prefix: '@', mode: 'user'},
];

/** One entry of the permission list stored at login (GeneralConstant.PERMISSIONS). */
export interface PermissionEntry {
  module: string;
  action: string;
  access: boolean;
  global: boolean;
}

/** Modules whose `read` endpoint each search mode calls. */
const MODE_MODULES: { mode: SearchMode; module: string }[] = [
  {mode: 'facility', module: 'res_partner_facility'},
  {mode: 'survey', module: 'survey'},
  {mode: 'invoice', module: 'account_invoice'},
  {mode: 'user', module: 'res_user'},
];

/**
 * Roles whose active-survey list the backend narrows to their own assignments. The active-survey
 * query also returns every in-progress survey regardless of that narrowing, so these roles get
 * no survey search.
 */
const NARROWED_SURVEY_ROLES = ['Staff', 'QUADA - Surveyor', 'Surveyor Lead'];

/**
 * Search modes the user can safely use. A mode needs a `read` permission granted by one of the
 * user's own roles (`global`): permissions that come only from assignments can be stale, and the
 * backend then answers 403, which logs the user out.
 */
export function allowedSearchModes(permissions: PermissionEntry[], roleNames: string[]): SearchMode[] {
  const canRead = (module: string) => permissions.some(p =>
    p.access && p.global && p.action?.toLowerCase() === 'read' && p.module?.toLowerCase() === module);
  return ['module', ...MODE_MODULES
    .filter(it => canRead(it.module))
    .filter(it => it.mode !== 'survey' || !roleNames.some(role => NARROWED_SURVEY_ROLES.includes(role)))
    .map(it => it.mode)];
}

/**
 * Works out what the sidebar search should look for. A prefix only switches mode when the user
 * may use that mode; otherwise the text, prefix included, is searched as module names.
 */
export function resolveSearch(raw: string, allowed: SearchMode[]): { mode: SearchMode; query: string } {
  const text = raw.trim();
  const match = SEARCH_PREFIXES.find(p => text.startsWith(p.prefix) && allowed.includes(p.mode));
  if (match) {
    return {mode: match.mode, query: text.substring(match.prefix.length).trim()};
  }
  return {mode: 'module', query: text};
}
